import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { businessDeliveryOperationsService as api } from '../../services/businessDeliveryOperationsService';
import { businessFulfillmentService } from '../../services/businessFulfillmentService';
import { needsDecision } from '../../utils/orderAttention';
import { logisticsBriefing, QUEUE_PAGE_LIMITS } from '../../utils/logisticsAttention';

const empty = { failedStops: [], riderReports: [], returnSessions: [], returnedParcels: [], cancellations: [], proofs: [], fulfillmentBlocked: 0 };
const list = (data) => Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

// What a person would call each source when it cannot be read.
const SOURCE_NAMES = Object.freeze({
  fulfillmentBlocked: 'blocked orders', failedStops: 'failed stops', riderReports: 'road reports',
  returnSessions: 'rider returns', returnedParcels: 'returned parcels', cancellations: 'cancellations after pickup',
  proofs: 'flagged proofs', board: 'trips', assignmentProblems: 'rider-reported problems',
});

function Row({ item, tone }) {
  const countTone = tone === 'decision'
    ? 'bg-warning-subtle text-warning'
    : 'bg-neutral-100 text-neutral-800 dark:bg-white/10 dark:text-neutral-100';
  return (
    <li>
      <Link to={item.to} className="group flex min-h-14 items-center gap-3 px-4 py-3 hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus">
        <span className={`inline-flex min-w-10 shrink-0 justify-center rounded-lg px-2 py-1 text-sm font-bold tabular-nums ${countTone}`}>
          {item.count}{item.atLeast ? '+' : ''}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold">{item.title}</span>
          <span className="block text-sm text-neutral-600 dark:text-neutral-300">
            {item.detail}
            {item.examples.length ? <span className="text-neutral-500 dark:text-neutral-400"> {item.examples.join(', ')}{item.count > item.examples.length ? ' and more' : ''}.</span> : null}
          </span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-neutral-400 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
      </Link>
    </li>
  );
}

export default function LogisticsAttention({ businessId, board, boardLoading, boardError, assignmentProblems,
  problemsError, canReadOrders, canManageExceptions, canReceiveReturns, canDecideReturns, canReviewProofs,
  canDecideCancellations, revision }) {
  const [state, setState] = useState({ businessId: null, data: empty, loading: true, loaded: false, failed: [] });
  const current = state.businessId === businessId ? state : { data: empty, loading: true, loaded: false, failed: [] };

  useEffect(() => {
    if (!businessId) return undefined;
    const controller = new AbortController();
    const signal = controller.signal;
    const sources = [
      // The queue's "needs attention" stage also holds orders that are only
      // waiting on a payment or already settled; those are not decisions.
      canReadOrders && ['fulfillmentBlocked', async () => list(
        await businessFulfillmentService.list({ stage: 'EXCEPTIONS', page: 1, limit: QUEUE_PAGE_LIMITS.fulfillment }, signal),
      ).filter(needsDecision).length],
      canManageExceptions && ['failedStops', async () => list(await api.stopDispositions(businessId, { signal }))],
      canManageExceptions && ['riderReports', async () => list(await api.riderReports(businessId, { signal }))],
      canReceiveReturns && ['returnSessions', async () => list(await api.returnSessions(businessId, { signal }))],
      canDecideReturns && ['returnedParcels', async () => list(await api.returnedParcels(businessId, { signal }))],
      canDecideCancellations && ['cancellations', async () => list(await api.postCustodyCancellations(businessId, { status: 'REQUESTED' }, { signal }))],
      canReviewProofs && ['proofs', async () => list(await api.proofs(businessId, { status: 'FINALIZED', limit: QUEUE_PAGE_LIMITS.proofs }, { signal }))],
    ].filter(Boolean);
    setState((previous) => (previous.businessId === businessId
      ? { ...previous, loading: true }
      : { businessId, data: empty, loading: true, loaded: false, failed: [] }));
    // Each request is wrapped so one that throws before returning a promise
    // is reported as a failed source instead of breaking the whole briefing.
    Promise.allSettled(sources.map(([, request]) => Promise.resolve().then(request))).then((results) => {
      if (signal.aborted) return;
      const data = { ...empty };
      const failed = [];
      results.forEach((result, index) => {
        const key = sources[index][0];
        if (result.status === 'fulfilled') data[key] = result.value;
        else failed.push(key);
      });
      setState({ businessId, data, loading: false, loaded: true, failed });
    });
    return () => controller.abort();
  }, [businessId, canReadOrders, canManageExceptions, canReceiveReturns, canDecideReturns, canReviewProofs, canDecideCancellations, revision]);

  const { decisions, watch, recent } = useMemo(() => logisticsBriefing({
    board: boardError ? {} : board,
    assignmentProblems: problemsError ? [] : assignmentProblems,
    ...current.data,
  }), [board, boardError, assignmentProblems, problemsError, current.data]);

  const unchecked = [
    ...(boardError ? ['board'] : []),
    ...(problemsError ? ['assignmentProblems'] : []),
    ...current.failed,
  ].map((key) => SOURCE_NAMES[key]);
  const firstLoad = !current.loaded || (boardLoading && !board?.businessId && !boardError);

  return (
    <div className="space-y-4">
      <section aria-labelledby="logistics-decisions-title" className="overflow-hidden rounded-panel border border-default bg-surface">
        <div className="flex min-h-14 items-center justify-between gap-3 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
          <h2 id="logistics-decisions-title" className="font-semibold">Waiting for a decision</h2>
          {current.loading && !firstLoad ? <Loader2 className="h-4 w-4 animate-spin text-neutral-400 motion-reduce:animate-none" aria-label="Refreshing" /> : null}
        </div>
        {firstLoad ? (
          <p role="status" className="flex items-center gap-2 px-4 py-4 text-sm text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> Checking delivery work…
          </p>
        ) : (
          <>
            {unchecked.length > 0 && (
              <p role="alert" className="flex gap-2 border-b border-warning bg-warning-subtle px-4 py-3 text-sm text-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>These could not be checked: {unchecked.join(', ')}. Refresh to try again.</span>
              </p>
            )}
            {decisions.length ? (
              <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {decisions.map((item) => <Row key={item.key} item={item} tone="decision" />)}
              </ul>
            ) : unchecked.length === 0 ? (
              <p className="flex items-center gap-2 px-4 py-4 text-sm text-neutral-700 dark:text-neutral-200">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                Nothing is waiting for a decision in the work you can access.
              </p>
            ) : null}
          </>
        )}
      </section>

      {!firstLoad && (watch.length > 0 || recent.length > 0) && (
        <section aria-labelledby="logistics-watch-title" className="overflow-hidden rounded-panel border border-default bg-surface">
          <h2 id="logistics-watch-title" className="border-b border-neutral-200 px-4 py-3 font-semibold dark:border-neutral-800">Worth a look</h2>
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {[...watch, ...recent].map((item) => <Row key={item.key} item={item} tone="watch" />)}
          </ul>
        </section>
      )}
    </div>
  );
}
